import React, { useState, useEffect } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { PiEyeClosedLight } from "react-icons/pi";
import { BsEye } from "react-icons/bs";
import { MdLock } from "react-icons/md";

/**
 * ReactInput - reusable input component
 *
 * Props (important ones):
 * - tagInput: boolean -> if true, shows tag/chips UI (type value + Enter or click +)
 * - reactSelect, creatable, isMulti, reactSelectOptions -> for react-select modes
 * - options -> normal <select> options
 * - onChange -> for tagInput: receives array of tags; for reactSelect: whatever react-select returns;
 *               for normal input/select: behaves like a normal onChange (event) unless caller handles differently.
 */
export const ReactInput = ({
    className,
    placeholder,
    label,
    name,
    value,
    onChange,
    error,
    type = "text",
    onBlur,
    mandatory,
    required,
    readOnly,
    max,
    min,
    maxLength,
    border,
    options = [], // normal select dropdown
    reactSelect = false, // react-select mode
    creatable = false, // enable CreatableSelect
    isMulti = false, // multiple select
    reactSelectOptions = [], // react-select options
    tagInput = false, // <<< NEW: enable tag/chips input mode
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword((prev) => !prev);
    const isPassword = type === "password";
    const isSelect = options.length > 0;

    // handle clear for react-select / creatable
    // const handleClear = () => {
    //     if (!readOnly && reactSelect) {
    //         onChange(isMulti ? [] : null);
    //     }
    // };

    //
    // ===== Tag input state & handlers =====
    //
    // tags: internal array of tags (keeps in sync with incoming `value` when value is array)
    const [tags, setTags] = useState(Array.isArray(value) ? value : []);
    const [tempValue, setTempValue] = useState("");

    // sync tags when parent updates `value`
    // normalize incoming value into array of strings
    useEffect(() => {
        if (!Array.isArray(value)) {
            setTags([]);
            return;
        }
        const normalized = value.map(v =>
            typeof v === "string"
                ? v
                : v && (v.label ?? v.value)
                    ? String(v.label ?? v.value)
                    : String(v)
        );
        setTags(normalized);
    }, [value]);

    // robust extractor: accepts undefined (use tempValue), string, event, or object
    const extractString = (input) => {
        // prefer explicit input if provided
        if (input === undefined || input === null) {
            return (tempValue || "").trim();
        }

        // if it's a DOM event (has target)
        if (typeof input === "object" && "target" in input && input.target) {
            const tv = input.target.value;
            return typeof tv === "string" ? tv.trim() : String(tv).trim();
        }

        // if object from react-select like {label, value}
        if (typeof input === "object") {
            if (input.label || input.value) {
                return String(input.label ?? input.value).trim();
            }
            // fallback (not ideal but prevents crashes)
            return String(input).trim();
        }

        // primitive (string/number/boolean)
        return String(input).trim();
    };

    const handleAddTag = (maybeInput) => {
        const v = extractString(maybeInput);
        if (!v) return;

        // duplicate prevention (case-insensitive)
        const exists = tags.some(t => t.toLowerCase() === v.toLowerCase());
        if (exists) {
            setTempValue("");
            return;
        }

        const newTags = [...tags, v];
        setTags(newTags);
        onChange && onChange(newTags); // pass normalized array to parent
        setTempValue("");
    };

    const handleRemoveTag = (index) => {
        const newTags = tags.filter((_, i) => i !== index);
        setTags(newTags);
        onChange && onChange(newTags);
    };
    //
    // ===== Render JSX =====
    //
    return (
        <div className="mb-3 relative">
            <label className="block text-sm font-medium text-gray-600 mb-1">
                {label}
                {!readOnly && mandatory && <span className="text-red-500 text-sm font-bold"> *</span>}
            </label>

            <div className="relative">
                {/* ReactSelect / Creatable */}
                {reactSelect ? (
                    <>
                        {creatable ? (
                            <CreatableSelect
                                isMulti={isMulti}
                                name={name}
                                value={value}
                                onChange={onChange}
                                options={reactSelectOptions || []}
                                className={`rounded-lg text-sm focus:outline-none ${error ? "border-red-500" : "border-gray-300"} ${className} ${border ? "border" : ""}`}
                                classNamePrefix="select"
                                placeholder={placeholder}
                                isDisabled={readOnly}
                            />
                        ) : (
                            <Select
                                isMulti={isMulti}
                                name={name}
                                value={value}
                                onChange={onChange}
                                options={reactSelectOptions || []}
                                className={`rounded-lg text-sm mt-1 focus:outline-none ${error ? "border-red-500" : "border-gray-300"} ${className} ${border ? "border" : ""}`}
                                classNamePrefix="select"
                                placeholder={`${placeholder}`}
                                isDisabled={readOnly}
                            />
                        )}

                        {/* Lock icon if readOnly */}
                        {readOnly && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                                <MdLock size={20} />
                            </div>
                        )}
                    </>
                ) : isSelect ? (
                    // Normal select
                    <div className="relative">
                        <select
                            name={name}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            required={required}
                            readOnly={readOnly || false}
                            className={`rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2
                                ${error ? "border-red-500" : value ? " text-black" : "border-gray-300 text-gray-400"}
                                ${border ? "border" : "border border-gray-300"}
                                ${className}`}
                        >
                            <option value="">{placeholder || "Select"}</option>
                            {options.map((opt, index) => (
                                <option key={index} value={opt.value || opt}>
                                    {opt.label || opt}
                                </option>
                            ))}
                        </select>

                        {readOnly && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                                <MdLock size={20} />
                            </div>
                        )}
                    </div>
                ) : tagInput ? (
                    // ===== Tag Input UI =====
                    <div className="relative">
                        <div className="flex gap-2">
                            <input
                                type={ type || "text"}
                                name={name}
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault(); // prevent form submit
                                        handleAddTag();
                                    }
                                }}
                                placeholder={placeholder || "Type and press Enter or click +"}
                                className="flex-1 rounded-lg px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2"
                                readOnly={readOnly || false}
                                maxLength={maxLength}
                            />
                            <button
                                type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleAddTag(); // do not pass e here
                                        }}
                                className={`px-3 py-2 rounded ${readOnly ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-primary-500 text-white hover:bg-primary-600"}`}
                                title="Add"
                                disabled={readOnly}
                            >
                                +
                            </button>
                        </div>

                        {/* chips */}
                                <div className={`${tags && tags.length > 0 ? "border p-2 rounded-xl" : ""} flex flex-wrap gap-2 mt-2 ${tags && tags.length > 0 ? "justify-start" : "justify-center"}`}>
                                    {tags && tags.length > 0 ? (
                                        tags.map((tag, i) => {
                                            const display = typeof tag === "string" ? tag : String(tag);
                                            return (
                                                <span
                                                    key={`${i}-${display}`}
                                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center gap-2"
                                                >
                                                    <span className="max-w-xs truncate">{display}</span>
                                                    {!readOnly && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveTag(i)}
                                                            className="text-red-500 hover:text-red-700"
                                                            aria-label={`Remove ${display}`}
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </span>
                                            );
                                        })
                                    ) : (
                                        // optional placeholder when no tags exist
                                        <div className="w-full text-sm text-gray-400 py-2">No items added</div>
                                    )}
                                </div>
                    </div>
                ) : (
                    // Normal input
                    <div className="relative">
                        <input
                            type={isPassword && showPassword ? "text" : type}
                            name={name}
                            value={value}
                            onChange={onChange}
                            placeholder={`${placeholder}`}
                            onBlur={onBlur}
                            required={required}
                            readOnly={readOnly || false}
                            max={max}
                            min={min}
                            maxLength={maxLength}
                            className={`rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${error ? "border-red-500" : "border-gray-300"} ${border ? "border" : "border border-gray-300"} ${className}`}
                        />

                        {/* Password eye toggle */}
                        {isPassword && !readOnly && (
                            <button
                                type="button"
                                onClick={togglePassword}
                                className="absolute inset-y-0 right-3 flex items-center"
                            >
                                {showPassword ? <BsEye size={20} /> : <PiEyeClosedLight size={20} />}
                            </button>
                        )}

                        {/* Lock icon for readOnly input */}
                        {readOnly && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <MdLock size={20} />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-100 rounded-md h-auto">
                    <p className="text-red-500 text-sm mt-1 ml-3">{error}</p>
                </div>
            )}
        </div>
    );
};


